import { SERVICE_MODE, validErrorCodes } from '../../constants';
import { IExceptionModel, ImportServiceOperation } from '../../models';

/**
 * @method serviceOperationImportMapper
 * @description Maps the file data to service operations, exceptions, and exception list.
 * @param fileData - The data from the file.
 * @returns An object containing the mapped service operations, exceptions, and exception list.
 */
export const serviceOperationImportMapper = (fileData: any) => {
  const serviceOperationsData: ImportServiceOperation[] = [];
  const serviceExceptionsData: {
    errorCode: string;
    exceptionName: string;
  }[] = [];
  let exceptionListData: string[] = [];

  if (fileData.paths && Object.keys(fileData.paths).length > 0) {
    Object.keys(fileData.paths).forEach((key) => {
      Object.keys(fileData.paths[key]).forEach((opVerb) => {
        serviceOperationsData.push(schemaOperationMapper(fileData.paths[key][`${opVerb}`], key, opVerb));
        const { exceptionArray, exceptionList } = serviceExceptionMapper(fileData.paths[key][`${opVerb}`]);
        exceptionListData = [...exceptionListData, ...exceptionList];
        if (exceptionArray.length > 0) serviceExceptionsData.push(...exceptionArray);
      });
    });
  }
  return { serviceOperationsData, serviceExceptionsData, exceptionListData };
};

/**
 * @method schemaOperationMapper
 * @description Maps the operation data to a schema object.
 * @param operationData - The operation data to be mapped.
 * @param key - The key for the operation URL.
 * @param opVerb - The operation verb.
 * @returns The mapped schema object.
 */
const schemaOperationMapper = (operationData: any, key: string, opVerb: string) => {
  const parameter = operationData?.parameters;
  return {
    serviceOperation: operationData?.operationId,
    operationUrl: key,
    operationVerb: opVerb.toUpperCase(),
    queryPathParam: parameter ? paramMapper(parameter, 'query') : [],
    headerParam: parameter ? paramMapper(parameter, 'header') : []
  };
};

/**
 * @method paramMapper
 * @description Maps the parameters based on the specified type.
 * @param parameters - The array of parameters.
 * @param type - The type of parameters to filter.
 * @returns An array of parameter headers.
 */
const paramMapper = (parameters: any, type: any) => {
  const paramHeaders: any = [];
  parameters.forEach((paramObj: { in: string, name: string }) => {
    if (paramObj.in === type && paramObj.name) {
      paramHeaders.push({
        key: paramObj.name,
        value: 'NULL',
      });
    }
  });
  return paramHeaders;
};

/**
 * @method serviceDetailImportMapper
 * @description Maps the imported service details from a file to a structured object.
 * @param fileData - The data imported from the file.
 * @returns An object containing the mapped service details.
 */
export const serviceDetailImportMapper = (fileData: any) => ({
  serviceName: fileData?.info?.title || '',
  endPoint: fileData?.servers && fileData?.servers[0]?.url ? fileData?.servers[0]?.url : '',
  serviceDescription: fileData?.info?.description || '',
  serviceType: 'REST',
  authName: ''
});

/**
 * @method serviceExceptionMapper
 * @description Maps service operation responses to an array of exceptions.
 * @param serviceOperation - The service operation object.
 * @returns An object containing an array of exceptions and a list of exception descriptions.
 */
const serviceExceptionMapper = (serviceOperation: any) => {
  const exceptionArray: { errorCode: string; exceptionName: string; }[] = [];
  const exceptionList: string[] = [];
  const { responses } = serviceOperation;
  if (responses) {
    Object.keys(responses).forEach((errCode) => {
      if (errCode && responses[errCode].description) {
        exceptionList.push(responses[errCode].description);
        exceptionArray.push({
          errorCode: errCode,
          exceptionName: responses[errCode].description
        });
      }
    });
  }
  return { exceptionArray, exceptionList };
};

/**
 * @method exceptionDataMapper
 * @description Maps exception data to a specific format based on validation rules.
 * @param exceptionData - The array of exception data to be mapped.
 * @param validatedExceptionList - The list of validated exceptions.
 * @returns An object containing the mapped exception data and invalid exceptions.
 */
export const exceptionDataMapper = (exceptionData: {
  errorCode: string;
  exceptionName: string;
}[], validatedExceptionList: string[]) => {
  const tempArray: string[] = [];
  let invalidExceptions: string[] = [];

  const mappedExceptionData: IExceptionModel[] = [];
  exceptionData.forEach((exception, val) => {
    if (validatedExceptionList.includes(exception.exceptionName)
      && !tempArray.includes(exception.exceptionName)
      && !tempArray.includes(exception.errorCode)
      && validErrorCodes.includes(exception.errorCode)
    ) {
      tempArray.push(exception.errorCode, exception.exceptionName);

      mappedExceptionData.push({
        id: val,
        exceptionId: exception.exceptionName,
        errorCode: +exception.errorCode,
        action: ['Notify'],
        isEdit: false,
      });
    } else {
      invalidExceptions.push(exception.exceptionName);
    }
  });
  invalidExceptions = invalidExceptions.length > 0 ? duplicateArrayStringRemoval(invalidExceptions) : invalidExceptions;
  return { mappedExceptionData, invalidExceptions };
};

/**
 * @method duplicateArrayStringRemoval
 * @description Removes duplicate strings from an array of strings.
 * @param arrayString - The array of strings to remove duplicates from.
 * @returns An array of strings with duplicate values removed.
 */
export const duplicateArrayStringRemoval = (arrayString: string[]): string[] => {
  const uniqueObject: { [key: string]: number } = {};
  arrayString.forEach((item) => {
    if (!uniqueObject.item) {
      uniqueObject[`${item}`] = 1;
    }
  });
  return Object.keys(uniqueObject);
};

/**
 * @method shouldShowNavigationAwayPopup
 * @description displays the navigation away popup
 * @param serviceMode service mode
 * @param isUpdateServiceSuccess isUpdateServiceSuccess
 * @param isActiveUpdate isActiveUpdate
 * @param isCreateClicked isCreateClicked
 * @returns true if condition true else false
 */
export const shouldShowNavigationAwayPopup = (serviceMode: string, isUpdateServiceSuccess: boolean, isActiveUpdate: boolean, isCreateClicked: boolean, isPatchUpdateCalled: boolean) =>
  ((serviceMode?.toLocaleLowerCase() === SERVICE_MODE.EDIT && (!isUpdateServiceSuccess && !isActiveUpdate)) || (serviceMode?.toLocaleLowerCase() === '' && !isCreateClicked)) && !isPatchUpdateCalled;
